/**
 * Workflow API Client
 * Handles all API calls related to workflows using the new atomic save endpoint
 */

import { BaseApiClient, ApiResponse } from './baseApiClient';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import {
  SaveWorkflowRequest,
  SaveWorkflowResponse,
  RefreshWorkflowResponse,
} from '../types/workflow.types';

/**
 * Convert PascalCase string to camelCase
 */
const toCamelCase = (str: string): string => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

/**
 * Recursively convert object keys from PascalCase to camelCase
 */
const convertKeysToCamelCase = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToCamelCase(item));
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const converted: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const camelKey = toCamelCase(key);
        converted[camelKey] = convertKeysToCamelCase(obj[key]);
      }
    }
    return converted;
  }

  return obj;
};

/**
 * Workflow API Client
 * Implements the new atomic workflow save/refresh endpoints
 */
export class WorkflowApiClient extends BaseApiClient {
  /**
   * Save complete workflow (nodes + edges) atomically
   * Uses the new backend endpoint: POST /api/v1/process-studio/workflows/save
   *
   * @param request - Complete workflow save request with nodes and edges
   * @returns Save response with operation summary and validation errors (if any)
   *
   * @example
   * ```typescript
   * const request: SaveWorkflowRequest = {
   *   processThreadID: 123,
   *   versionID: 1,
   *   nodes: [...],
   *   edges: [...]
   * };
   * const response = await workflowApiClient.saveWorkflow(request);
   * if (response.success && response.data) {
   *   console.log(`Saved: ${response.data.summary.nodesCreated} nodes created`);
   * }
   * ```
   */
  async saveWorkflow(request: SaveWorkflowRequest): Promise<ApiResponse<SaveWorkflowResponse>> {
    const response = await this.post<SaveWorkflowResponse>(API_ENDPOINTS.WORKFLOWS.SAVE, request);

    // Convert PascalCase keys to camelCase for frontend consumption
    if (response.success && response.data) {
      const camelCaseData = convertKeysToCamelCase(response.data);
      return {
        ...response,
        data: camelCaseData as SaveWorkflowResponse,
      };
    }

    return response;
  }

  /**
   * Refresh workflow data from server
   * Uses the backend endpoint: GET /api/v1/process-studio/workflows/{processThreadId}/refresh
   *
   * @param processThreadId - The ProcessThread ID to refresh
   * @returns Refreshed workflow data with current nodes and edges from database
   *
   * @example
   * ```typescript
   * const response = await workflowApiClient.refreshWorkflow(123);
   * if (response.success && response.data) {
   *   const { nodes, edges } = response.data;
   *   // Update UI with fresh data
   * }
   * ```
   */
  async refreshWorkflow(processThreadId: number): Promise<ApiResponse<RefreshWorkflowResponse>> {
    const response = await this.get<RefreshWorkflowResponse>(API_ENDPOINTS.WORKFLOWS.REFRESH(processThreadId));

    // Convert PascalCase keys to camelCase for frontend consumption
    if (response.success && response.data) {
      // DEBUG: Log original response structure
      console.log('[workflowApiClient.refreshWorkflow] Original response:', {
        nodeCount: response.data?.nodes?.length || 0,
        firstNodeKeys: response.data?.nodes?.[0] ? Object.keys(response.data.nodes[0]) : [],
        firstNodeSample: response.data?.nodes?.[0] ? {
          ProcessElementID: (response.data.nodes[0] as any).ProcessElementID,
          ProcessElementKey: (response.data.nodes[0] as any).ProcessElementKey,
          ConnectorID: (response.data.nodes[0] as any).ConnectorID,
        } : null,
      });

      const camelCaseData = convertKeysToCamelCase(response.data);

      // DEBUG: Log converted response
      console.log('[workflowApiClient.refreshWorkflow] Converted response:', {
        nodeCount: (camelCaseData as any).nodes?.length || 0,
        firstNodeKeys: (camelCaseData as any).nodes?.[0] ? Object.keys((camelCaseData as any).nodes[0]) : [],
        firstNodeSample: (camelCaseData as any).nodes?.[0] ? {
          processElementID: (camelCaseData as any).nodes[0].processElementID,
          processElementKey: (camelCaseData as any).nodes[0].processElementKey,
          connectorID: (camelCaseData as any).nodes[0].connectorID,
        } : null,
      });

      return {
        ...response,
        data: camelCaseData as RefreshWorkflowResponse,
      };
    }

    return response;
  }
}

/**
 * Singleton instance
 * Import and use this instance throughout the application
 */
export const workflowApiClient = new WorkflowApiClient();
